import { Divider } from "@mui/material";
import { Severity } from "node-library/dist/constants";
import { FrontendClient, FrontendOrganization } from "node-library/dist/portal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { GreenButton } from "../../../../common-components/button";
import CountryProvinceSelector from "../../../../common-components/countryAndProvinceSelector";
import {
	CommonSwitch,
	CustomTextInput,
} from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { validateEmail } from "../../../../helpers/inputHelper";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";

interface Props {
	addOrEditClient: (client: FrontendClient) => void;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	organization: FrontendOrganization;
	client?: FrontendClient;
}

export default function AddOrEditClientModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { open, setOpen, organization } = props;

	const [email, setEmail] = useState<string>(props.client?.email || "");
	const [firstName, setFirstName] = useState<string>(
		props.client?.firstName || ""
	);
	const [lastName, setLastName] = useState<string>(
		props.client?.lastName || ""
	);

	const [phoneNumber, setPhoneNumber] = useState<string>(
		props.client?.phoneNumber || ""
	);

	const [defaultUseSameBillingInfo, setDefaultUseSameBillingInfo] =
		useState<boolean>(!(props.client?.defaultUseDifferentBillingInfo || false));

	const [defaultBillingName, setDefaultBillingName] = useState<string>(
		props.client?.defaultBillingName || ""
	);
	const [defaultBillingAddress, setDefaultBillingAddress] = useState<string>(
		props.client?.defaultBillingAddress || ""
	);
	const [defaultBillingCity, setDefaultBillingCity] = useState<string>(
		props.client?.defaultBillingCity || ""
	);
	const [defaultBillingProvince, setDefaultBillingProvince] = useState<string>(
		props.client?.defaultBillingProvince || ""
	);
	const [defaultBillingPostalCode, setDefaultBillingPostalCode] =
		useState<string>(props.client?.defaultBillingPostalCode || "");
	const [defaultBillingCountry, setDefaultBillingCountry] = useState<string>(
		props.client?.defaultBillingCountry || ""
	);
	const [defaultBillingPhoneNumber, setDefaultBillingPhoneNumber] =
		useState<string>(props.client?.defaultBillingPhoneNumber || "");
	const [defaultBillingEmail, setDefaultBillingEmail] = useState<string>(
		props.client?.defaultBillingEmail || ""
	);

	const [loading, setLoading] = useState<boolean>(false);
	//validations
	const [emailErrorMsg, setEmailErrorMsg] = useState<string>("");
	const [firstNameErrorMsg, setFirstNameErrorMsg] = useState<string>("");
	const [lastNameErrorMsg, setLastNameErrorMsg] = useState<string>("");

	const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState<string>("");

	const [defaultBillingNameErrorMsg, setDefaultBillingNameErrorMsg] =
		useState<string>("");
	const [defaultBillingAddressErrorMsg, setDefaultBillingAddressErrorMsg] =
		useState<string>("");
	const [defaultBillingCityErrorMsg, setDefaultBillingCityErrorMsg] =
		useState<string>("");
	const [
		defaultBillingPostalCodeErrorMsg,
		setDefaultBillingPostalCodeErrorMsg,
	] = useState<string>("");
	const [defaultBillingProvinceErrorMsg, setDefaultBillingProvinceErrorMsg] =
		useState<string>("");
	const [defaultBillingCountryErrorMsg, setDefaultBillingCountryErrorMsg] =
		useState<string>("");
	const [
		defaultBillingPhoneNumberErrorMsg,
		setDefaultBillingPhoneNumberErrorMsg,
	] = useState<string>("");
	const [defaultBillingEmailErrorMsg, setDefaultBillingEmailErrorMsg] =
		useState<string>("");

	const fieldsForRegistration: Array<any> = [
		{
			id: "client-firstname-input",

			label: `${translation("FirstName")}`,
			inputType: "text",
			value: firstName,
			setValue: setFirstName,
			required: true,
			errorMsg: firstNameErrorMsg,
			setErrorMsg: setFirstNameErrorMsg,
		},
		{
			id: "client-lastname-input",

			label: `${translation("LastName")}`,
			inputType: "text",
			value: lastName,
			setValue: setLastName,
			required: false,
			errorMsg: lastNameErrorMsg,
			setErrorMsg: setLastNameErrorMsg,
		},
		{
			id: "client-email-input",
			label: `${translation("Email")}`,
			inputType: "email",
			value: email,
			setValue: setEmail,
			required: false,
			errorMsg: emailErrorMsg,
			setErrorMsg: setEmailErrorMsg,
		},
		{
			id: "client-phone-input",

			label: `${translation("PhoneNumber")}`,
			inputType: "tel",
			value: phoneNumber,
			setValue: setPhoneNumber,
			required: false,
			errorMsg: phoneNumberErrorMsg,
			setErrorMsg: setPhoneNumberErrorMsg,
		},
	];
	const billingInformationFields1: Array<any> = [
		{
			id: "default-billing-name-input",
			label: translation("PersonalOrCompanyName"),
			setValue: (value: string) => {
				setDefaultBillingName(value);
			},
			value: defaultBillingName,
			required: !defaultUseSameBillingInfo,

			errorMsg: defaultBillingNameErrorMsg,
			setErrorMsg: setDefaultBillingNameErrorMsg,
		},
	];
	const billingInformationFields2: Array<any> = [
		{
			id: "default-billing-address-input",
			label: translation("PersonalOrCompanyAddress"),
			setValue: (value: string) => {
				setDefaultBillingAddress(value);
			},
			value: defaultBillingAddress,
			required: !defaultUseSameBillingInfo,

			errorMsg: defaultBillingAddressErrorMsg,
			setErrorMsg: setDefaultBillingAddressErrorMsg,
		},
	];
	const billingInformationFields3: Array<any> = [
		{
			id: "default-billing-city-input",
			label: translation("City"),
			setValue: (value: string) => {
				setDefaultBillingCity(value);
			},
			value: defaultBillingCity,
			required: !defaultUseSameBillingInfo,

			errorMsg: defaultBillingCityErrorMsg,
			setErrorMsg: setDefaultBillingCityErrorMsg,
		},
		{
			id: "default-billing-postal-code-input",
			label: translation("PostalCode"),
			setValue: (value: string) => {
				setDefaultBillingPostalCode(value);
			},
			value: defaultBillingPostalCode,
			required: !defaultUseSameBillingInfo,

			errorMsg: defaultBillingPostalCodeErrorMsg,
			setErrorMsg: setDefaultBillingPostalCodeErrorMsg,
		},
	];
	const billingInfoForCountryAndProvince = [
		{
			id: "default-billing-country-input",
			label: translation("Country"),
			setValue: (value: string) => {
				setDefaultBillingCountry(value);
			},
			value: defaultBillingCountry,
			required: !defaultUseSameBillingInfo,

			errorMsg: defaultBillingCountryErrorMsg,
			setErrorMsg: setDefaultBillingCountryErrorMsg,
		},
		{
			id: "default-billing-province-input",
			label: translation("Province"),
			setValue: (value: string) => {
				setDefaultBillingProvince(value);
			},
			value: defaultBillingProvince,
			required: !defaultUseSameBillingInfo,

			errorMsg: defaultBillingProvinceErrorMsg,
			setErrorMsg: setDefaultBillingProvinceErrorMsg,
		},
	];
	const billingInformationFields4: Array<any> = [
		{
			id: "default-billing-phone-number-input",
			label: translation("PhoneNumber"),
			setValue: (value: string) => {
				setDefaultBillingPhoneNumber(value);
			},
			value: defaultBillingPhoneNumber,
			required: !defaultUseSameBillingInfo,

			errorMsg: defaultBillingPhoneNumberErrorMsg,
			setErrorMsg: setDefaultBillingPhoneNumberErrorMsg,
		},
		{
			id: "default-billing-email-input",
			label: translation("Email"),
			setValue: (value: string) => {
				setDefaultBillingEmail(value);
			},
			value: defaultBillingEmail,
			required: !defaultUseSameBillingInfo,

			errorMsg: defaultBillingEmailErrorMsg,
			setErrorMsg: setDefaultBillingEmailErrorMsg,
		},
	];

	const allFields = [
		...fieldsForRegistration,
		...billingInformationFields1,
		...billingInformationFields2,
		...billingInformationFields3,
		...billingInformationFields4,
		...billingInfoForCountryAndProvince,
	];

	useEffect(() => {
		if (open) {
			for (const field of allFields) {
				field.setErrorMsg(translation(""));
			}
			setEmail(props.client?.email || "");
			setFirstName(props.client?.firstName || "");
			setLastName(props.client?.lastName || "");

			setPhoneNumber(props.client?.phoneNumber || "");
		}
	}, [open]);

	function handleSubmit() {
		let hasError: boolean = false;
		for (const field of allFields) {
			if (
				field.required &&
				(!field.value || (field.value as string).trim().length == 0)
			) {
				field.setErrorMsg(translation("MissingInformation"));
				hasError = true;
			} else {
				field.setErrorMsg("");
			}
		}

		if (hasError) {
			return;
		}

		if (phoneNumber && phoneNumber.trim().length < 7) {
			setPhoneNumberErrorMsg("InvalidPhoneNumber");
			return;
		}
		if (email && !validateEmail(email)) {
			setEmailErrorMsg(translation("InvalidEmail"));
			return;
		}
		if (props.client) {
			const updatedClient: FrontendClient = {
				...props.client,
				email,
				firstName,
				lastName,
				phoneNumber,
				defaultUseDifferentBillingInfo: !defaultUseSameBillingInfo,
				defaultBillingName,
				defaultBillingAddress,
				defaultBillingCity,
				defaultBillingProvince,
				defaultBillingCountry,
				defaultBillingPostalCode,
				defaultBillingPhoneNumber,
				defaultBillingEmail,
			};
			editClient(updatedClient);
		} else {
			addClient();
		}
		setOpen(false);
	}

	async function addClient() {
		setLoading(true);
		try {
			const response = await sendPOSTRequest("/clients/add-client", {
				email,
				firstName,
				lastName,
				phoneNumber,
				organizationId: organization.organizationId,

				defaultUseDifferentBillingInfo: !defaultUseSameBillingInfo,
				defaultBillingName,
				defaultBillingAddress,
				defaultBillingCity,
				defaultBillingProvince,
				defaultBillingCountry,
				defaultBillingPostalCode,
				defaultBillingPhoneNumber,
				defaultBillingEmail,
			});

			const clientId: string = response?.data?.clientId;

			let newClient: FrontendClient = {
				clientId: clientId,
				email,
				firstName,
				lastName,
				phoneNumber,
				associationId: organization.associationId,

				defaultUseDifferentBillingInfo: !defaultUseSameBillingInfo,
				defaultBillingName,
				defaultBillingAddress,
				defaultBillingCity,
				defaultBillingProvince,
				defaultBillingCountry,
				defaultBillingPostalCode,
				defaultBillingPhoneNumber,
				defaultBillingEmail,

				inserted: new Date(),
			};

			props.addOrEditClient(newClient);
			openSnackbar(
				translation("NewClientAdded"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			setOpen(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	async function editClient(updatedClient: FrontendClient) {
		setLoading(true);
		try {
			const response = await sendPOSTRequest("/clients/edit-client", {
				frontendClient: updatedClient,
			});

			props.addOrEditClient(updatedClient);
			openSnackbar(
				translation("ClientInformationUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			setOpen(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	const registrationInputs: Array<React.ReactNode> = fieldsForRegistration.map(
		(field, index) => {
			return (
				<CustomTextInput
					id={field.id}
					key={field.id}
					value={field.value}
					setValue={field.setValue}
					disabled={field.disabled}
					label={field.label}
					type={field.inputType}
					required={field.required}
					errorMsg={field.errorMsg}
					setErrorMsg={field.setErrorMsg}
				></CustomTextInput>
			);
		}
	);

	const registrationContent: React.ReactNode = (
		<div className="registration-input-content">
			<div className="register-grid-row">
				{registrationInputs[0]}
				{registrationInputs[1]}
			</div>
			<div className="register-grid-row">
				{registrationInputs[2]}
				{registrationInputs[3]}
			</div>
			<div className="profile-billing-info-same-info-switch">
				<div className="typography-body-01">
					{translation("UseCompanyInformationForBilling")}
				</div>
				<CommonSwitch
					id="company-info-for-billing-switch"
					checked={defaultUseSameBillingInfo}
					setChecked={(checked: boolean) => {
						setDefaultUseSameBillingInfo(checked);
					}}
				/>
			</div>
			<div
				style={{
					display: defaultUseSameBillingInfo ? "none" : "initial",
				}}
			>
				<Divider orientation="horizontal" flexItem sx={{ my: 2 }}></Divider>

				<div>
					{billingInformationFields1.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
				<div>
					{billingInformationFields2.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
				<div className="register-grid-row">
					{billingInformationFields3.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}

					<CountryProvinceSelector
						countryDropdownId={"default-billing-country-input"}
						provinceDropdownId={"default-billing-province-input"}
						selectedCountryName={defaultBillingCountry}
						setSelectedCountryName={setDefaultBillingCountry}
						selectedState={defaultBillingProvince}
						setSelectedState={setDefaultBillingProvince}
						setUnsavedChanges={() => {}}
					></CountryProvinceSelector>
				</div>
				<div className="register-grid-row">
					{billingInformationFields4.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
			</div>
		</div>
	);

	const confirmApprovalModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={
				props.client ? translation("EditClient") : translation("NewClient")
			}
			// headerDescription={translation("ConfirmApprovalDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-client-change-button"
					id="submit-client-change-button"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : props.client ? (
							translation("Edit")
						) : (
							translation("Add")
						)
					}
					disabled={loading}
					onClick={handleSubmit}
				></GreenButton>,
			]}
		>
			{registrationContent}
		</ModalWithButtons>
	);
	return confirmApprovalModal;
}
